import { useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { selectedAssemblyId } from '@/components/Analysis/state/atoms';
import { selectedPointOfInterestId } from '@/components/Analysis/modules/pointOfInterest/state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { ReportInternalType, useAllReportsListQuery } from '@/__generated__/graphql';

export type ReportTypeTab = 'ALL' | 'COMPONENT' | 'POINT_OF_INTEREST' | 'PAINT_BLOCK';

const TAB_TO_INTERNAL_TYPE: Record<ReportTypeTab, ReportInternalType | undefined> = {
  ALL: undefined,
  COMPONENT: ReportInternalType.Component,
  POINT_OF_INTEREST: ReportInternalType.PointOfInterest,
  PAINT_BLOCK: ReportInternalType.PaintBlock,
};

type UseGetReportsOptions = {
  componentId?: string;
  pointOfInterestId?: string;
  initialTab?: ReportTypeTab;
};

export const useGetReports = (options: UseGetReportsOptions = {}) => {
  const selectedAssembly = useAtomValue(selectedAssemblyId);
  const selectedPoi = useAtomValue(selectedPointOfInterestId);
  const assemblyId = options.componentId ?? selectedAssembly;
  const poiId = options.pointOfInterestId ?? selectedPoi;
  const structureId = useGetStructureId();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<ReportTypeTab>(options.initialTab ?? 'ALL');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    if (options.initialTab) {
      setActiveTab(options.initialTab);
      setPage(1);
    }
  }, [options.initialTab]);

  const internalType =
    activeTab === 'POINT_OF_INTEREST' && poiId
      ? undefined
      : TAB_TO_INTERNAL_TYPE[activeTab];

  const { data, loading, error } = useAllReportsListQuery({
    variables: {
      input: {
        structureId,
        componentIds: assemblyId ? [assemblyId] : undefined,
        internalType,
        name: searchTerm || undefined,
      },
    },
    skip: !structureId || !assemblyId,
    fetchPolicy: 'no-cache',
  });

  const allReports = useMemo(() => {
    return (data?.allReports ?? []).filter(Boolean);
  }, [data?.allReports]);

  // Classify independently: a report can be both component- and POI-linked; the old reduce
  // returned early on component match and hid POI-tab results for the same report.
  const componentReports = useMemo(() => {
    if (!assemblyId) return [];
    return allReports.filter(
      (report) =>
        report &&
        Array.isArray(report.components) &&
        report.components.some((c) => c?.id === assemblyId)
    );
  }, [allReports, assemblyId]);

  const pointOfInterestReports = useMemo(() => {
    if (!poiId) return [];
    return allReports.filter(
      (report) =>
        report &&
        Array.isArray(report.pointOfInterests) &&
        report.pointOfInterests.some((p) => p?.id === poiId)
    );
  }, [allReports, poiId]);

  const paintBlockReports = useMemo(
    () =>
      allReports.filter(
        (report) => report && report.internalType === ReportInternalType.PaintBlock
      ),
    [allReports]
  );

  const otherReports = useMemo(() => {
    const componentIds = new Set(componentReports.map((r) => r?.id).filter(Boolean));
    const poiIds = new Set(pointOfInterestReports.map((r) => r?.id).filter(Boolean));
    const paintIds = new Set(paintBlockReports.map((r) => r?.id).filter(Boolean));
    return allReports.filter(
      (report) =>
        report &&
        !componentIds.has(report.id) &&
        !poiIds.has(report.id) &&
        !paintIds.has(report.id)
    );
  }, [allReports, componentReports, pointOfInterestReports, paintBlockReports]);

  const reports = useMemo(() => {
    if (activeTab === 'ALL') return allReports;
    if (activeTab === 'COMPONENT') return componentReports;
    if (activeTab === 'POINT_OF_INTEREST') return pointOfInterestReports;
    if (activeTab === 'PAINT_BLOCK') return paintBlockReports;
    return allReports;
  }, [activeTab, allReports, componentReports, pointOfInterestReports, paintBlockReports]);

  const paginatedReports = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return reports.slice(start, start + rowsPerPage);
  }, [reports, page, rowsPerPage]);

  const totalPages = Math.ceil(reports.length / rowsPerPage);

  const handleTabChange = (tab: ReportTypeTab) => {
    setActiveTab(tab);
    setPage(1);
  };

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    setPage(1);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  return {
    reports: paginatedReports,
    totalReports: reports.length,
    loading,
    error,
    searchTerm,
    setSearchTerm: handleSearchChange,
    activeTab,
    setActiveTab: handleTabChange,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage: handleRowsPerPageChange,
    totalPages,
    componentReports,
    pointOfInterestReports,
    paintBlockReports,
    otherReports,
  };
};
